import { useEffect, useRef } from "react";

const TABBABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable=\"true\"]",
  "[tabindex]",
].join(",");

function getTabbableElements(container) {
  return Array.from(container.querySelectorAll(TABBABLE_SELECTOR)).filter((element) => {
    if (element.disabled || element.hidden || element.getAttribute("aria-hidden") === "true") {
      return false;
    }
    return element.tabIndex >= 0;
  });
}

function focusElement(element) {
  if (!element || typeof element.focus !== "function") return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

/**
 * Keeps keyboard focus inside a container while active.
 *
 * The trigger is the element focused when activation starts. It is restored
 * when the trap is deactivated or unmounted. `active` may be replaced with
 * `disabled`, and an options object is intentionally kept stable through refs
 * so changing onEscape does not recreate the document listener.
 */
export function useFocusTrap(containerRef, options = {}, legacyOnEscape) {
  const config = typeof options === "object" && options !== null ? options : {};
  const active = typeof options === "boolean"
    ? options
    : config.active ?? !config.disabled;
  const onEscape = legacyOnEscape ?? config.onEscape;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active || typeof document === "undefined" || !containerRef?.current) {
      return undefined;
    }

    const container = containerRef.current;
    const trigger = document.activeElement;
    const focusables = getTabbableElements(container);
    focusElement(focusables[0]);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onEscapeRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      const currentFocusables = getTabbableElements(container);
      if (currentFocusables.length === 0) return;

      const currentIndex = currentFocusables.indexOf(document.activeElement);
      const isWrappingForward = !event.shiftKey && (
        currentIndex === currentFocusables.length - 1 || currentIndex === -1
      );
      const isWrappingBackward = event.shiftKey && (currentIndex <= 0);
      if (!isWrappingForward && !isWrappingBackward) return;

      event.preventDefault();
      focusElement(isWrappingBackward
        ? currentFocusables[currentFocusables.length - 1]
        : currentFocusables[0]);
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (trigger && trigger.isConnected !== false) focusElement(trigger);
    };
  }, [active, containerRef]);
}
